import React, { useState, useEffect, useCallback } from 'react';
import { FiCreditCard, FiCheck, FiTrendingUp, FiBarChart2, FiZap, FiFolder, FiLock } from 'react-icons/fi';
import { getAuthHeaders } from '../utils/auth';
import { getApiEndpoint } from '../utils/api';
import analytics from '../utils/analytics';
import './Membership.css';

const Membership = ({ onBack, onEnterpriseClick }) => {
  const [subscription, setSubscription] = useState(null);
  const [usage, setUsage] = useState(null);
  const [plans, setPlans] = useState([]);
  const [payments, setPayments] = useState([]);
  const [enterpriseAccounts, setEnterpriseAccounts] = useState([]);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [upgrading, setUpgrading] = useState(false);
  const [error, setError] = useState(null);
  const [billingCycle, setBillingCycle] = useState('monthly'); // 'monthly' or 'yearly'

  // Password change state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordChanging, setPasswordChanging] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState(null);

  const fetchCurrentUser = async () => {
    try {
      const response = await fetch(getApiEndpoint('/api/auth/me'), {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setCurrentUserId(data.id);
      }
    } catch (err) {
      console.error('Failed to fetch current user:', err);
    }
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchCurrentUser(),
        fetchSubscription(),
        fetchUsage(),
        fetchPlans(),
        fetchPayments(),
        fetchEnterpriseAccounts()
      ]);
    } catch (err) {
      setError('Failed to load membership data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  const syncPayments = useCallback(async () => {
    try {
      const response = await fetch(getApiEndpoint('/api/user/payments/sync'), {
        method: 'POST',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        const data = await response.json();
        await fetchPayments(); // Refresh payment list
        alert(data.message || `Synced ${data.synced_count} payment(s) from Stripe`);
      } else {
        const errorData = await response.json();
        alert(errorData.detail || 'Failed to sync payments');
      }
    } catch (err) {
      alert('Failed to sync payments');
      console.error(err);
    }
  }, []);

  const updateSubscriptionFromStripe = useCallback(async (sessionId) => {
    try {
      const response = await fetch(getApiEndpoint('/api/user/subscription/update-from-stripe'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify({ session_id: sessionId })
      });
      
      if (response.ok) {
        const data = await response.json();
        // Refresh subscription data by calling fetchData which includes fetchSubscription
        await fetchData();
        // Auto-sync payments after successful subscription update
        try {
          await syncPayments();
        } catch (syncErr) {
          // Don't fail the whole flow if sync fails
          console.error('Failed to auto-sync payments:', syncErr);
        }
        alert(`Success! Your subscription has been updated to ${data.plan}.`);
      } else {
        const errorData = await response.json();
        alert(errorData.detail || 'Failed to update subscription. Please contact support.');
      }
    } catch (err) {
      console.error('Failed to update subscription:', err);
      alert('Failed to update subscription. Please refresh the page or contact support.');
    }
  }, [fetchData, syncPayments]);

  useEffect(() => {
    fetchData();
    
    // Check if we're returning from Stripe checkout
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    
    if (sessionId) {
      // Update subscription from Stripe session
      updateSubscriptionFromStripe(sessionId);
      
      // Clean up URL
      window.history.replaceState({}, '', '/membership');
    }
  }, [fetchData, updateSubscriptionFromStripe]);

  const fetchEnterpriseAccounts = async () => {
    try {
      const response = await fetch(getApiEndpoint('/api/enterprise/accounts'), {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setEnterpriseAccounts(data);
      }
    } catch (err) {
      console.error('Failed to fetch enterprise accounts:', err);
    }
  };

  const fetchSubscription = async () => {
    try {
      const response = await fetch(getApiEndpoint('/api/user/subscription'), {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setSubscription(data);
      }
    } catch (err) {
      console.error('Failed to fetch subscription:', err);
    }
  };

  const fetchUsage = async () => {
    try {
      const response = await fetch(getApiEndpoint('/api/user/usage'), {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setUsage(data);
      }
    } catch (err) {
      console.error('Failed to fetch usage:', err);
    }
  };

  const fetchPlans = async () => {
    try {
      const response = await fetch(getApiEndpoint('/api/subscription/plans'));
      if (response.ok) {
        const data = await response.json();
        // Only show the three main tiers: Free, Pro, and Enterprise
        const validPlans = data.filter(plan => 
          plan.slug === 'free' || plan.slug === 'pro' || plan.slug === 'enterprise'
        );
        setPlans(validPlans);
      }
    } catch (err) {
      console.error('Failed to fetch plans:', err);
    }
  };

  const fetchPayments = async () => {
    try {
      const response = await fetch(getApiEndpoint('/api/user/payments'), {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setPayments(data);
      }
    } catch (err) {
      console.error('Failed to fetch payments:', err);
    }
  };

  const handleUpgrade = async (planSlug, billingCycle = 'monthly') => {
    analytics.track('subscription_checkout_clicked', {
      plan: planSlug,
      billing_cycle: billingCycle,
    });
    setUpgrading(true);
    setError(null);
    try {
      // For Enterprise plan, ask for member count
      if (planSlug === 'enterprise') {
        const memberCountStr = prompt('Enter number of members/seats for your Enterprise subscription:');
        if (!memberCountStr) {
          setUpgrading(false);
          return;
        }
        const memberCount = parseInt(memberCountStr);
        if (isNaN(memberCount) || memberCount < 1) {
          alert('Please enter a valid number of members (at least 1)');
          setUpgrading(false);
          return;
        }
        
        // Use enterprise checkout endpoint
        const response = await fetch(getApiEndpoint('/api/enterprise/checkout'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...getAuthHeaders()
          },
          body: JSON.stringify({
            member_count: memberCount,
            billing_cycle: billingCycle
          })
        });

        if (response.ok) {
          const data = await response.json();
          // Redirect to Stripe checkout
          window.location.href = data.checkout_url;
        } else {
          const errorData = await response.json();
          setError(errorData.detail || 'Failed to create checkout session');
          setUpgrading(false);
        }
      } else {
        // For individual plans (Free/Pro), use regular checkout
        const response = await fetch(getApiEndpoint('/api/user/subscription/checkout'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...getAuthHeaders()
          },
          body: JSON.stringify({
            plan_slug: planSlug,
            billing_cycle: billingCycle
          })
        });

        if (response.ok) {
          const data = await response.json();
          // Redirect to Stripe checkout
          window.location.href = data.checkout_url;
        } else {
          const errorData = await response.json();
          setError(errorData.detail || 'Failed to create checkout session');
          setUpgrading(false);
        }
      }
    } catch (err) {
      setError('Failed to upgrade subscription');
      console.error(err);
      setUpgrading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPasswordMessage(null);

    if (newPassword !== confirmPassword) {
      setPasswordMessage({ type: 'error', text: 'New passwords do not match' });
      return;
    }

    if (newPassword.length < 6) {
      setPasswordMessage({ type: 'error', text: 'New password must be at least 6 characters' });
      return;
    }

    setPasswordChanging(true);
    try {
      const response = await fetch(getApiEndpoint('/api/auth/change-password'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify({
          current_password: currentPassword,
          new_password: newPassword
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to change password');
      }

      setPasswordMessage({ type: 'success', text: 'Password changed successfully' });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      setPasswordMessage({ type: 'error', text: err.message });
    } finally {
      setPasswordChanging(false);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm('Are you sure you want to cancel your subscription? You will retain access until the end of your billing period.')) {
      return;
    }

    try {
      const response = await fetch(getApiEndpoint('/api/user/subscription/cancel'), {
        method: 'POST',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        await fetchSubscription();
        alert('Subscription will be canceled at the end of the billing period');
      } else {
        const errorData = await response.json();
        alert(errorData.detail || 'Failed to cancel subscription');
      }
    } catch (err) {
      alert('Failed to cancel subscription');
      console.error(err);
    }
  };

  const handleResume = async () => {
    try {
      const response = await fetch(getApiEndpoint('/api/user/subscription/resume'), {
        method: 'POST',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        await fetchSubscription();
        alert('Subscription resumed');
      } else {
        const errorData = await response.json();
        alert(errorData.detail || 'Failed to resume subscription');
      }
    } catch (err) {
      alert('Failed to resume subscription');
      console.error(err);
    }
  };


  const formatPrice = (cents) => {
    if (cents === 0) return 'Free';
    return `$${(cents / 100).toFixed(2)}`;
  };

  const formatUsage = (current, limit, isChatbotTokens = false, planSlug = null) => {
    // Free plan cannot have unlimited chatbot tokens
    if (isChatbotTokens && planSlug === 'free' && (limit === null || limit === undefined || limit === 0)) {
      limit = 20000; // Default limit for Free plan
    }
    
    if (limit === null || limit === undefined) return `${current} (Unlimited)`;
    const percentage = Math.min((current / limit) * 100, 100);
    return `${current} / ${limit} (${percentage.toFixed(0)}%)`;
  };

  const getUsagePercentage = (current, limit, isChatbotTokens = false, planSlug = null) => {
    // Free plan cannot have unlimited chatbot tokens
    if (isChatbotTokens && planSlug === 'free' && (limit === null || limit === undefined || limit === 0)) {
      limit = 20000; // Default limit for Free plan
    }
    
    if (limit === null || limit === undefined) return 0;
    return Math.min((current / limit) * 100, 100);
  };

  const formatFeatureValue = (plan, field, value) => {
    // Free plan cannot have unlimited chatbot tokens - always show a limit
    if (plan.slug === 'free' && field === 'chatbot_tokens_per_month') {
      const limit = value ?? 20000;
      return limit.toLocaleString();
    }
    
    if (value === null || value === undefined) return 'Unlimited';
    
    if (typeof value === 'number') {
      if (field.includes('chatbot_tokens')) {
        return value.toLocaleString();
      }
      
      if (field.includes('storage')) {
        return Number.isInteger(value) ? value.toLocaleString() : value.toFixed(2);
      }
      
      return value.toLocaleString();
    }
    
    return value;
  };

  if (loading) {
    return (
      <div className="membership-container">
        <div className="loading">Loading membership information...</div>
      </div>
    );
  }

  const currentPlan = subscription?.plan;
  const isPro = currentPlan?.slug === 'pro';
  const isFree = currentPlan?.slug === 'free';
  const isEnterprise = enterpriseAccounts.length > 0 && enterpriseAccounts.some(acc => acc.status === 'active');
  const isCanceled = subscription?.cancel_at_period_end || subscription?.status === 'canceled';

  return (
    <div className="membership-container ph-no-capture">
      {onBack && (
        <button className="back-button" onClick={onBack}>
          ← Back
        </button>
      )}

      <div className="membership-header">
        <h1>Membership & Billing</h1>
        <p>Manage your subscription and view usage</p>
      </div>

      {error && (
        <div className="error-message">{error}</div>
      )}

      {/* Current Plan */}
      <div className="membership-section">
        <h2>Current Plan</h2>
        <div className="current-plan-card">
          <div className="plan-header">
            <div>
              <h3>{currentPlan?.name || 'Free'}</h3>
              <p className="plan-price">
                {isEnterprise ? (
                  <>Per member pricing</>
                ) : (
                  <>{formatPrice(currentPlan?.price_monthly || 0)}/month</>
                )}
              </p>
              {isEnterprise && enterpriseAccounts.length > 0 && (
                <p style={{ fontSize: '0.875rem', color: '#666', marginTop: '0.25rem' }}>
                  You are a member of {enterpriseAccounts.length} enterprise account{enterpriseAccounts.length > 1 ? 's' : ''}
                </p>
              )}
            </div>
            <div className="plan-status">
              {isCanceled ? (
                <span className="status-badge canceled">Canceled</span>
              ) : (
                <span className="status-badge active">Active</span>
              )}
            </div>
          </div>
          {subscription && (
            <div className="plan-details">
              {subscription.current_period_end && (
                <p>
                  {isCanceled ? 'Access until' : 'Renews on'}:{' '}
                  {new Date(subscription.current_period_end).toLocaleDateString()}
                </p>
              )}
              {isCanceled && !isFree && !isEnterprise && (
                <button className="resume-button" onClick={handleResume}>
                  Resume Subscription
                </button>
              )}
              {!isCanceled && !isFree && !isEnterprise && (
                <button className="cancel-button" onClick={handleCancel}>
                  Cancel Subscription
                </button>
              )}
              {isEnterprise && (
                <p style={{ fontSize: '0.875rem', color: '#666', marginTop: '0.5rem' }}>
                  Enterprise subscription is managed by the account owner. See Enterprise Membership section below.
                </p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Usage Dashboard */}
      {usage && (
        <div className="membership-section">
          <h2>Usage This Month</h2>
          <div className="usage-grid">
            <div className="usage-card">
              <div className="usage-header">
                <FiBarChart2 />
                <span>Searches</span>
              </div>
              <div className="usage-value">
                {formatUsage(usage.searches_count, usage.limits?.searches_per_month)}
              </div>
              <div className="usage-bar">
                <div 
                  className="usage-fill"
                  style={{ width: `${getUsagePercentage(usage.searches_count, usage.limits?.searches_per_month)}%` }}
                />
              </div>
            </div>

            <div className="usage-card">
              <div className="usage-header">
                <FiFolder />
                <span>Collections</span>
              </div>
              <div className="usage-value">
                {formatUsage(usage.collections_created, usage.limits?.collections)}
              </div>
              <div className="usage-bar">
                <div 
                  className="usage-fill"
                  style={{ width: `${getUsagePercentage(usage.collections_created, usage.limits?.collections)}%` }}
                />
              </div>
            </div>

            <div className="usage-card">
              <div className="usage-header">
                <FiZap />
                <span>Chatbot Tokens</span>
              </div>
              <div className="usage-value">
                {formatUsage(usage.chatbot_tokens_used, usage.limits?.chatbot_tokens_per_month, true, subscription?.plan?.slug)}
              </div>
              <div className="usage-bar">
                <div 
                  className="usage-fill"
                  style={{ width: `${getUsagePercentage(usage.chatbot_tokens_used, usage.limits?.chatbot_tokens_per_month, true, subscription?.plan?.slug)}%` }}
                />
              </div>
            </div>

            <div className="usage-card">
              <div className="usage-header">
                <FiTrendingUp />
                <span>Storage</span>
              </div>
              <div className="usage-value">
                {formatUsage(usage.storage_gb?.toFixed(2), usage.limits?.storage_gb)}
              </div>
              <div className="usage-bar">
                <div 
                  className="usage-fill"
                  style={{ width: `${getUsagePercentage(usage.storage_gb, usage.limits?.storage_gb)}%` }}
                />
              </div>
            </div>

            <div className="usage-card">
              <div className="usage-header">
                <FiCreditCard />
                <span>PDF Uploads</span>
              </div>
              <div className="usage-value">
                {formatUsage(usage.pdf_uploads_count, usage.limits?.pdf_uploads_per_month)}
              </div>
              <div className="usage-bar">
                <div 
                  className="usage-fill"
                  style={{ width: `${getUsagePercentage(usage.pdf_uploads_count, usage.limits?.pdf_uploads_per_month)}%` }}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Plans */}
      {!isEnterprise && (
        <div className="membership-section">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
            <h2 style={{ margin: 0 }}>
              {subscription?.status === 'trialing' ? 'Activate Your Subscription' : 'Change Plan'}
            </h2>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '0.9rem', color: billingCycle === 'monthly' ? '#333' : '#999', fontWeight: billingCycle === 'monthly' ? '500' : '400' }}>Monthly</span>
              <label style={{ 
                position: 'relative', 
                display: 'inline-block', 
                width: '52px', 
                height: '28px',
                cursor: 'pointer'
              }}>
                <input
                  type="checkbox"
                  checked={billingCycle === 'yearly'}
                  onChange={(e) => setBillingCycle(e.target.checked ? 'yearly' : 'monthly')}
                  style={{ opacity: 0, width: 0, height: 0, position: 'absolute' }}
                />
                <span style={{
                  position: 'absolute',
                  cursor: 'pointer',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: billingCycle === 'yearly' ? '#667eea' : '#ccc',
                  borderRadius: '28px',
                  transition: 'background-color 0.3s ease'
                }}>
                  <span style={{
                    position: 'absolute',
                    height: '22px',
                    width: '22px',
                    left: '3px',
                    top: '3px',
                    backgroundColor: 'white',
                    borderRadius: '50%',
                    transition: 'transform 0.3s ease',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                    transform: billingCycle === 'yearly' ? 'translateX(24px)' : 'translateX(0)'
                  }} />
                </span>
              </label>
              <span style={{ fontSize: '0.9rem', color: billingCycle === 'yearly' ? '#333' : '#999', fontWeight: billingCycle === 'yearly' ? '500' : '400' }}>Yearly</span>
            </div>
          </div>
          <div className="plans-grid">
            {plans.map(plan => {
              // Hide free plan for paid/trialing users — downgrading means canceling (handled above)
              if (plan.slug === 'free' && !isFree) return null;

              const isCurrentPlan = plan.slug === currentPlan?.slug;
              const isTrialing = subscription?.status === 'trialing';
              const isEnterprisePlan = plan.slug === 'enterprise';

              // For Enterprise plan, show per-member pricing
              const chatbotLimit = isEnterprisePlan
                ? (plan.max_enterprise_chatbot_tokens_per_month ?? plan.max_chatbot_tokens_per_month)
                : plan.max_chatbot_tokens_per_month;
              const storageLimit = isEnterprisePlan
                ? (plan.max_enterprise_storage_gb ?? plan.max_storage_gb)
                : plan.max_storage_gb;
              const pdfLimit = isEnterprisePlan
                ? (plan.max_enterprise_pdf_uploads_per_month ?? plan.max_pdf_uploads_per_month)
                : plan.max_pdf_uploads_per_month;

              // Determine price based on billing cycle
              const price = billingCycle === 'yearly' && plan.price_yearly
                ? plan.price_yearly
                : plan.price_monthly;
              const monthlyEquivalent = billingCycle === 'yearly' && plan.price_yearly
                ? Math.round(plan.price_yearly / 12)
                : plan.price_monthly;

              // Button label logic
              let buttonLabel;
              let buttonDisabled = upgrading || (billingCycle === 'yearly' && !plan.price_yearly);
              if (isCurrentPlan && isTrialing) {
                buttonLabel = upgrading ? 'Processing...' : 'Subscribe Now →';
              } else if (isCurrentPlan) {
                buttonLabel = 'Current Plan';
                buttonDisabled = true;
              } else if (isEnterprisePlan) {
                buttonLabel = upgrading ? 'Processing...' : 'Switch to Enterprise';
              } else {
                buttonLabel = upgrading ? 'Processing...' :
                  (billingCycle === 'yearly' && !plan.price_yearly) ? 'Yearly not available' :
                  `Upgrade to ${plan.name}`;
              }


              return (
                <div key={plan.id} className={`plan-card${isCurrentPlan ? ' plan-card-current' : ''}`}>
                  <div className="plan-card-header">
                    <h3>{plan.name}{isCurrentPlan && isTrialing && <span className="trial-badge"> · Trial</span>}</h3>
                    <div className="plan-price-large">
                      {isEnterprisePlan ? (
                        <>
                          {formatPrice(price)}
                          <span className="plan-period">
                            /member/{billingCycle === 'yearly' ? 'year' : 'month'}
                          </span>
                          {billingCycle === 'yearly' && plan.price_yearly && (
                            <div style={{ fontSize: '0.75rem', color: '#666', marginTop: '0.25rem' }}>
                              (${(monthlyEquivalent / 100).toFixed(2)}/month)
                            </div>
                          )}
                        </>
                      ) : (
                        <>
                          {formatPrice(price)}
                          <span className="plan-period">
                            /{billingCycle === 'yearly' ? 'year' : 'month'}
                          </span>
                          {billingCycle === 'yearly' && plan.price_yearly && (
                            <div style={{ fontSize: '0.75rem', color: '#666', marginTop: '0.25rem' }}>
                              (${(monthlyEquivalent / 100).toFixed(2)}/month)
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                  <ul className="plan-features">
                    {isEnterprisePlan ? (
                      <>
                        <li>
                          <FiCheck /> Unlimited searches/month
                        </li>
                        <li>
                          <FiCheck /> Unlimited collections
                        </li>
                        <li>
                          <FiCheck /> Unlimited items per collection
                        </li>
                        <li>
                          <FiCheck /> {formatFeatureValue(plan, 'enterprise_chatbot_tokens_per_month', chatbotLimit)} chatbot tokens/month
                        </li>
                        <li>
                          <FiCheck /> {formatFeatureValue(plan, 'enterprise_storage_gb', storageLimit)} GB storage
                        </li>
                        <li>
                          <FiCheck /> {formatFeatureValue(plan, 'enterprise_pdf_uploads_per_month', pdfLimit)} PDF uploads/month
                        </li>
                        <li>
                          <FiCheck /> Per-member pricing - add/remove members anytime
                        </li>
                      </>
                    ) : (
                      <>
                        <li>
                          <FiCheck /> {formatFeatureValue(plan, 'searches_per_month', plan.max_searches_per_month)} searches/month
                        </li>
                        <li>
                          <FiCheck /> {formatFeatureValue(plan, 'collections', plan.max_collections)} collections
                        </li>
                        <li>
                          <FiCheck /> {formatFeatureValue(plan, 'items_per_collection', plan.max_items_per_collection)} items per collection
                        </li>
                        <li>
                          <FiCheck /> {formatFeatureValue(plan, 'chatbot_tokens_per_month', chatbotLimit)} chatbot tokens/month
                        </li>
                        <li>
                          <FiCheck /> {formatFeatureValue(plan, 'storage_gb', storageLimit)} GB storage
                        </li>
                        <li>
                          <FiCheck /> {formatFeatureValue(plan, 'pdf_uploads_per_month', pdfLimit)} PDF uploads/month
                        </li>
                      </>
                    )}
                  </ul>
                  <button
                    className={`upgrade-button${isCurrentPlan && isTrialing ? ' upgrade-button-primary' : ''}`}
                    onClick={() => handleUpgrade(plan.slug, billingCycle)}
                    disabled={buttonDisabled}
                  >
                    {buttonLabel}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Enterprise Accounts */}
      {enterpriseAccounts.length > 0 && (
        <div className="membership-section">
          <h2>Enterprise Membership</h2>
          <div className="enterprise-accounts-list">
            {enterpriseAccounts.map(account => {
              // Check if current user is the owner
              const isOwner = account.owner_id === currentUserId;
              
              return (
                <div key={account.id} className="enterprise-account-card">
                  <div className="enterprise-account-info">
                    <h3>{account.name}</h3>
                    <p>
                      <strong>Your Role:</strong> {isOwner ? 'Owner' : 'Member'}
                    </p>
                    <p>{account.current_member_count} / {account.purchased_member_count} members</p>
                    <p>Status: {account.status}</p>
                    {account.cancel_at_period_end && (
                      <p style={{ color: '#856404' }}>Will cancel at period end</p>
                    )}
                  </div>
                  {onEnterpriseClick && (
                    <button
                      className="view-enterprise-button"
                      onClick={() => onEnterpriseClick(account.id)}
                    >
                      {isOwner ? 'Manage' : 'View Details'}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Payment History */}
      <div className="membership-section">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h2 style={{ margin: 0 }}>Payment History</h2>
          {(subscription?.stripe_subscription_id || subscription?.stripe_customer_id || (subscription?.plan?.slug !== 'free' && subscription?.status === 'active')) && (
            <button 
              onClick={syncPayments}
              className="sync-payments-button"
              style={{ 
                padding: '0.5rem 1rem',
                backgroundColor: '#667eea',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '0.875rem',
                fontWeight: '500'
              }}
            >
              Sync from Stripe
            </button>
          )}
        </div>
        {payments.length > 0 ? (
          <div className="payments-table">
            <table>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th>Invoice</th>
                </tr>
              </thead>
              <tbody>
                {payments.map(payment => (
                  <tr key={payment.id}>
                    <td>{new Date(payment.created_at).toLocaleDateString()}</td>
                    <td>{formatPrice(payment.amount)}</td>
                    <td>
                      <span className={`status-badge ${payment.status}`}>
                        {payment.status}
                      </span>
                    </td>
                    <td>
                      {payment.receipt_url ? (
                        <a 
                          href={payment.receipt_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="receipt-link"
                        >
                          Download Receipt
                        </a>
                      ) : payment.provider_invoice_id ? (
                        <a 
                          href={`https://invoice.stripe.com/i/${payment.provider_invoice_id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="receipt-link"
                        >
                          View Invoice
                        </a>
                      ) : (
                        <span className="no-receipt">N/A</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="no-payments-message">
            <p>No payment history yet. Payments will appear here after you upgrade to a paid plan.</p>
          </div>
        )}
      </div>

      {/* Account Settings */}
      <div className="membership-section">
        <h2><FiLock style={{ marginRight: '0.5rem' }} />Change Password</h2>
        <form onSubmit={handleChangePassword} className="password-change-form">
          {passwordMessage && (
            <div className={`password-message ${passwordMessage.type}`}>
              {passwordMessage.text}
            </div>
          )}
          <div className="form-group">
            <label>Current Password</label>
            <input
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              required
              placeholder="Enter current password"
            />
          </div>
          <div className="form-group">
            <label>New Password</label>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              minLength={6}
              placeholder="Enter new password (min 6 characters)"
            />
          </div>
          <div className="form-group">
            <label>Confirm New Password</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              placeholder="Confirm new password"
            />
          </div>
          <button
            type="submit"
            className="change-password-button"
            disabled={passwordChanging}
          >
            {passwordChanging ? 'Changing...' : 'Change Password'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default Membership;

